//@ts-check
import {ButtonIcon} from '@/components-v2/atoms/ButtonIcon/ButtonIcon'
import {FileUpload} from '@/components-v2/molecules/FileUpload/FileUpload'
import {generateVideoThumbnailFromUrl} from '@/helpers/mediaThumbnail'
import {useSortable} from '@dnd-kit/react/sortable'
import {useEffect, useState} from 'react'

/**
 * Module-level cache of generated thumbnails, keyed by the source video URL. This survives
 * unmounts/remounts of `MediaSlotFileUpload` (e.g. switching away from the Media tab and back),
 * so the same video isn't re-downloaded/re-decoded to produce a thumbnail every time. Entries are
 * intentionally never revoked while cached - they're reused for the lifetime of the page/session.
 */
const thumbnailCache = new Map()

/**
 * Renders a single media slot's FileUpload control.
 *
 * For video slots, `slot.mediaUrl` points at the actual video (either a freshly selected file's
 * local blob URL or an already-saved CDN URL) rather than an image, so it can't be used directly
 * as the FileUpload preview. This generates a still-frame thumbnail from that URL - the same
 * approach used by `MediaSlotItem` - so a preview image is shown instead of a broken/raw video source.
 */
export const MediaSlotFileUpload = ({slot, isEditMode, handleUpload, handleUploadError, handleDelete}) => {
  const isVideoSlot = slot.mediaType === 'VIDEO'
  const {ref, handleRef, isDragging} = useSortable({
    id: slot.id,
    index: slot.displayOrder - 1,
    disabled: !isEditMode,
  })
  const [videoThumbnailUri, setVideoThumbnailUri] = useState(() =>
    isVideoSlot && slot.mediaUrl ? thumbnailCache.get(slot.mediaUrl) || '' : ''
  )

  useEffect(() => {
    if (!isVideoSlot || !slot.mediaUrl) {
      setVideoThumbnailUri('')
      return
    }

    const cachedThumbnailUri = thumbnailCache.get(slot.mediaUrl)
    if (cachedThumbnailUri) {
      setVideoThumbnailUri(cachedThumbnailUri)
      return
    }

    let isMounted = true

    const createThumbnail = async () => {
      try {
        const generatedThumbnailUri = await generateVideoThumbnailFromUrl(slot.mediaUrl, {
          maxWidth: 468,
          maxHeight: 300,
          outputType: 'image/jpeg',
          quality: 0.85,
          captureAtSecond: 1,
        })

        thumbnailCache.set(slot.mediaUrl, generatedThumbnailUri)

        if (isMounted) {
          setVideoThumbnailUri(generatedThumbnailUri)
        }
      } catch {
        if (isMounted) {
          setVideoThumbnailUri('')
        }
      }
    }

    createThumbnail()

    return () => {
      isMounted = false
    }
  }, [isVideoSlot, slot.mediaUrl])

  const displayImageUri = isVideoSlot ? videoThumbnailUri : slot.mediaUrl || ''

  return (
    <div ref={ref} className={`relative flex flex-col gap-1 ${isDragging ? 'opacity-50' : ''}`}>
      {isEditMode && (
        <div
          title="Drag to reorder media files"
          ref={handleRef}
          className="absolute left-3 top-3 z-10 cursor-grab touch-none"
        >
          <ButtonIcon
            className="cursor-grab"
            icon="drag_indicator"
            size="medium"
            aria-label={`Reorder media slot ${slot.displayOrder}`}
          />
        </div>
      )}
      <FileUpload
        accept={slot.accept}
        description={`Drag and drop your file here (only ${slot.accept.split(',').join(', ')})`}
        buttonLabel="Select from System Library"
        variant={slot.uploading ? 'uploading' : slot.mediaUrl ? 'complete' : 'edit'}
        type="image"
        imageUri={displayImageUri}
        files={[]}
        feedbackType={slot.feedbackType}
        helperText={slot.helperText}
        disabled={!isEditMode}
        maxFiles={1}
        onUpdate={file => handleUpload(slot.displayOrder, file)}
        onDelete={() => handleDelete(slot.displayOrder)}
        onFileError={error => handleUploadError(slot.displayOrder, error)}
        data-cy={`media-slot-${slot.displayOrder}`}
      />
    </div>
  )
}
